import { useT } from '@/i18n/use-t';
import { useState } from 'react';
import {
  terminalParamIsActive,
  type TerminalParamDescriptor,
  type TerminalParamValue,
} from '@reform-lite/contracts';
import { useHasPermission } from '@/auth/auth-store';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { extractApiError } from '@/lib/api-error';
import { useSyncLag } from '@/hooks/use-kitchen';
import {
  useAllTerminalParams,
  useClearTerminalParam,
  useSetTerminalParam,
  useTerminalParamHistory,
  useTerminalParamImpact,
  useTerminalParamRegistry,
  type TerminalParamAssignment,
  type TerminalParamImpact,
} from '@/hooks/use-terminal-params';
import { CopyFromProfile } from './copy-from-profile';
import { targetScopeId, type EditorTarget } from './editor-target';
import { ParamField } from './param-field';
import {
  clearOutcome,
  clearWords,
  delivered,
  keepsCurrent,
  resolveRow,
  sourceLabel,
  sourceWords,
  type RowResolution,
} from './param-resolution';
import {
  formatParamValue,
  groupDescriptors,
  groupLabel,
  validateParamValue,
  valueWords,
} from './param-value';
import { ReturnReasonDirectoryView } from './return-reason-tree';
import { SecretParamControl } from './secret-param-control';

/**
 * Key-specific read-only views (TP-FR-015). Exactly one entry, and it stays
 * that way: `returns.reasons` is a machine-owned JSON tree a generic string
 * field cannot render. A second entry here would be the per-key UI cost this
 * module exists to end — a parameter that needs its own widget needs its own
 * value type in the contract.
 */
/** The translator `useT` hands back, for the few helpers that take it as an argument. */
type Translate = ReturnType<typeof useT>;

const SPECIAL_VIEWS: Record<
  string,
  (props: {
    descriptor: TerminalParamDescriptor;
    assignment: TerminalParamAssignment | undefined;
  }) => JSX.Element
> = {
  'returns.reasons': ReturnReasonDirectoryView,
};

/**
 * The registry-driven parameter editor (RL-273, TP-FR-009, TP-FR-011).
 *
 * One component, pinned to one scope target, rendered in three placements:
 * Settings → Parameters behind a scope selector, the terminal-profile page
 * pinned to a profile, the terminal detail page pinned to a terminal. There
 * is no other way to edit a parameter.
 *
 * Everything on screen comes from `GET /terminal-params/registry`: the
 * groups are the registry's own groups in the registry's own order, the
 * fields are generated per value type, and a descriptor's `scopes` decides
 * whether it is offered here at all. Nothing in this file names a key.
 */

export function ParameterEditor({
  target,
  attention = 'target',
}: {
  target: EditorTarget;
  /**
   * Which authored-but-undelivered and deprecated rows to list for attention
   * (TP-FR-024, TP-FR-025): only this target's, or the whole estate's. The
   * Parameters page shows the estate; a pinned placement shows its own.
   */
  attention?: 'target' | 'estate';
}) {
  const t = useT();
  const canRead = useHasPermission('terminal-params.read');
  const canWrite = useHasPermission('terminal-params.write');
  const registry = useTerminalParamRegistry(canRead);
  const assignments = useAllTerminalParams(canRead);

  if (!canRead) {
    return (
      <p className="text-sm text-destructive">
        {t('parameters.page.noAccess', 'You do not have permission to view terminal parameters.')}
      </p>
    );
  }
  if (registry.isLoading || assignments.isLoading) {
    return (
      <p className="text-sm text-muted-foreground">
        {t('parameters.editor.loading', 'Loading parameters…')}
      </p>
    );
  }
  if (registry.isError || registry.data === undefined || assignments.isError) {
    return (
      <div>
        <p className="text-sm text-destructive">
          {t('parameters.editor.loadError', 'Could not load parameters.')}
        </p>
        <Button
          className="mt-3"
          variant="outline"
          onClick={() => {
            void registry.refetch();
            void assignments.refetch();
          }}
        >
          {t('parameters.editor.retry', 'Retry')}
        </Button>
      </div>
    );
  }

  const rows = assignments.data ?? [];
  const resolutions = new Map(
    registry.data.descriptors.map((descriptor) => [
      descriptor.key,
      resolveRow(descriptor, rows, target),
    ]),
  );
  const resolutionFor = (key: string): RowResolution => {
    const resolution = resolutions.get(key);
    if (resolution === undefined) throw new Error(`no resolution for ${key}`);
    return resolution;
  };

  // A descriptor's `scopes` is authoritative (TP-FR-005). A deprecated key is
  // shown only while it still carries a value at this scope, so the value can
  // be retired (TP-FR-024).
  const visible = registry.data.descriptors.filter(
    (descriptor) =>
      descriptor.scopes.includes(target.scope) &&
      (!descriptor.deprecated || resolutionFor(descriptor.key).setHere !== undefined),
  );
  const groups = groupDescriptors(visible);

  // VL-365: a parameter that only acts while another one holds certain values
  // (`activeWhen`) is shown disabled, with the reason, wherever that other
  // parameter's value in effect at this target is something else.
  const byKey = new Map(registry.data.descriptors.map((d) => [d.key, d]));
  const inactiveFor = (descriptor: TerminalParamDescriptor): InactiveReason | undefined => {
    const rule = descriptor.activeWhen;
    if (rule === undefined) return undefined;
    const controlling = resolutions.get(rule.key)?.effective.value;
    if (terminalParamIsActive(descriptor, controlling)) return undefined;
    const controller = byKey.get(rule.key);
    return {
      controllerLabel: t(`params.${rule.key}.label`, controller?.label ?? rule.key),
      values: rule.values,
    };
  };

  return (
    <div className="space-y-5">
      <p className="text-xs text-muted-foreground">
        {t('parameters.editor.registryVersion', 'Registry v')}
        {registry.data.version} · {visible.length}{' '}
        {t('parameters.editor.parametersAt', 'parameters at')} {scopeLabel(t, target)} ·{' '}
        {t('parameters.editor.timing', TIMING_STATEMENT)}
      </p>
      {canWrite && target.scope === 'profile' && (
        <CopyFromProfile toProfileId={target.profileId} toProfileName={target.profileName} />
      )}
      <AttentionSection
        rows={
          attention === 'estate'
            ? rows
            : rows.filter(
                (row) => row.scope === target.scope && row.scopeId === targetScopeId(target),
              )
        }
        descriptors={registry.data.descriptors}
        canWrite={canWrite}
      />
      {groups.map((group) => (
        <section key={group.group} className="rounded-lg border bg-card">
          <h2 className="border-b px-5 py-3 text-sm font-semibold">{groupLabel(group.group, t)}</h2>
          <div className="divide-y">
            {group.descriptors.map((descriptor) => (
              <ParameterRow
                key={`${target.scope}:${targetScopeId(target)}:${descriptor.key}`}
                descriptor={descriptor}
                target={target}
                resolution={resolutionFor(descriptor.key)}
                canWrite={canWrite}
                inactive={inactiveFor(descriptor)}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function scopeLabel(t: Translate, target: EditorTarget): string {
  switch (target.scope) {
    case 'global':
      return t('parameters.editor.scopeGlobal', 'global scope');
    case 'profile':
      return `${t('parameters.editor.scopeProfile', 'profile')} ${target.profileName}`;
    case 'terminal':
      return `${t('parameters.editor.scopeTerminal', 'terminal')} ${target.terminalLabel}`;
  }
}

type Draft = { value: TerminalParamValue; error: string | null };

/** Why an `activeWhen` parameter has no effect at this target (VL-365). */
type InactiveReason = { controllerLabel: string; values: readonly string[] };

/**
 * Authored rows that need a human (TP-FR-024, TP-FR-025): rows the resolver
 * drops — key gone from the registry, type drifted, scope no longer
 * permitted, value invalid — and rows whose key is deprecated but still
 * authored. Each is shown with its scope, value and reason, and can be
 * cleared. Listing them changes nothing about resolution (TP-FR-026).
 */
function AttentionSection({
  rows,
  descriptors,
  canWrite,
}: {
  rows: TerminalParamAssignment[];
  descriptors: TerminalParamDescriptor[];
  canWrite: boolean;
}) {
  const t = useT();
  const byKey = new Map(descriptors.map((descriptor) => [descriptor.key, descriptor]));
  const flagged = rows
    .map((row) => {
      const descriptor = byKey.get(row.key);
      const reason = !delivered(row)
        ? `${t('parameters.attention.undelivered', 'Authored but not delivered:')} ${
            row.undelivered ??
            t('parameters.attention.resolverDrops', 'the resolver drops this row')
          }.`
        : descriptor?.deprecated === true
          ? t(
              'parameters.attention.deprecated',
              'Deprecated in the registry; still delivered until it is cleared.',
            )
          : null;
      return reason === null ? null : { row, descriptor, reason };
    })
    .filter((entry): entry is NonNullable<typeof entry> => entry !== null);
  if (flagged.length === 0) return null;
  return (
    <section
      className="rounded-lg border border-amber-400/60 bg-card"
      aria-label={t('parameters.attention.label', 'Assignments needing attention')}
    >
      <h2 className="border-b px-5 py-3 text-sm font-semibold">
        {t('parameters.attention.title', 'Needs attention')}
      </h2>
      <ul className="divide-y">
        {flagged.map(({ row, descriptor, reason }) => (
          <AttentionRow
            key={row.id}
            row={row}
            descriptor={descriptor}
            reason={reason}
            canWrite={canWrite}
          />
        ))}
      </ul>
    </section>
  );
}

function AttentionRow({
  row,
  descriptor,
  reason,
  canWrite,
}: {
  row: TerminalParamAssignment;
  descriptor: TerminalParamDescriptor | undefined;
  reason: string;
  canWrite: boolean;
}) {
  const t = useT();
  const clearParam = useClearTerminalParam();
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const label = descriptor?.label ?? row.key;
  const where =
    row.scope === 'global'
      ? t('parameters.scope.global', 'global')
      : `${
          row.scope === 'profile'
            ? t('parameters.editor.scopeProfile', 'profile')
            : t('parameters.editor.scopeTerminal', 'terminal')
        } ${row.scopeName ?? row.scopeId}`;
  const value =
    descriptor === undefined
      ? JSON.stringify(row.value)
      : formatParamValue(descriptor, row.value, valueWords(t));
  const name = `${label} ${t('parameters.common.at', 'at')} ${where}`;
  return (
    <li
      className="space-y-2 px-5 py-3 text-sm"
      data-attention={`${row.key}:${row.scope}:${row.scopeId}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{label}</span>
        <span className="font-mono text-xs text-muted-foreground">{row.key}</span>
        <Badge variant="outline">{where}</Badge>
        <span className="text-xs text-muted-foreground">
          {t('parameters.attention.value', 'value:')} {value}
        </span>
      </div>
      <p className="text-xs text-muted-foreground">{reason}</p>
      {error !== null && <p className="text-xs text-destructive">{error}</p>}
      {canWrite && !confirming && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          aria-label={`${t('parameters.editor.clearAction', 'Clear')} ${name}`}
          onClick={() => setConfirming(true)}
        >
          {t('parameters.editor.clear', 'Clear…')}
        </Button>
      )}
      {canWrite && confirming && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs">
            {t('parameters.attention.removes', 'Removes this authored value at')} {where}.
          </span>
          <Button
            type="button"
            size="sm"
            variant="destructive"
            aria-label={`${t('parameters.editor.confirmClear', 'Confirm clear')} ${name}`}
            disabled={clearParam.isPending}
            onClick={async () => {
              setError(null);
              try {
                await clearParam.mutateAsync({
                  key: row.key,
                  scope: row.scope,
                  scopeId: row.scopeId,
                });
              } catch (cause) {
                setError(extractApiError(cause));
              }
            }}
          >
            {clearParam.isPending
              ? t('parameters.editor.clearing', 'Clearing…')
              : t('parameters.editor.clearValue', 'Clear value')}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            aria-label={`${t('parameters.editor.keep', 'Keep')} ${name}`}
            onClick={() => setConfirming(false)}
          >
            {t('parameters.editor.keep', 'Keep')}
          </Button>
        </div>
      )}
    </li>
  );
}

/**
 * Shown for every parameter, not a marked subset (TP-FR-020, TP-FR-021):
 * there is no risk taxonomy, and a warning that appears only sometimes
 * teaches an operator that its absence means safety.
 */
export const TIMING_STATEMENT =
  'Reaches affected tills within about 30 seconds and may take effect mid-shift.';

/**
 * The timing for a parameter the till holds across an open shift (RL-267,
 * `appliesAt: 'shiftBoundary'`). TP-FR-020's general statement would be wrong
 * for these keys, so the editor replaces it rather than adding to it.
 */
export const SHIFT_BOUNDARY_TIMING_STATEMENT =
  'Reaches affected tills within about 30 seconds, but a till with an open shift holds the change and applies it at the next shift boundary, once the shift is closed.';

function timingStatement(t: Translate, descriptor: TerminalParamDescriptor): string {
  return descriptor.appliesAt === 'shiftBoundary'
    ? t('parameters.editor.timingShiftBoundary', SHIFT_BOUNDARY_TIMING_STATEMENT)
    : t('parameters.editor.timing', TIMING_STATEMENT);
}

/**
 * The blast radius in one sentence, split between terminals that will change
 * and terminals a lower scope already decides (TP-FR-018). A bare count
 * overstates reach at global scope, and a number that overstates itself
 * trains operators to ignore it.
 */
export function impactSentence(impact: TerminalParamImpact, target: EditorTarget): string {
  if (target.scope === 'terminal') return 'This till will change.';
  const count = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`;
  const reach =
    target.scope === 'profile'
      ? `${count(impact.terminals, 'terminal')} on this profile`
      : count(impact.terminals, 'terminal');
  const below = target.scope === 'profile' ? 'a terminal value' : 'a profile or terminal value';
  const verb = impact.overriddenBelow === 1 ? 'has' : 'have';
  return `${reach}, of which ${impact.willChange} will change. ${impact.overriddenBelow} ${verb} ${below} that takes precedence.`;
}

function ImpactNote({
  descriptor,
  target,
}: {
  descriptor: TerminalParamDescriptor;
  target: EditorTarget;
}) {
  const t = useT();
  const impact = useTerminalParamImpact({
    key: descriptor.key,
    scope: target.scope,
    scopeId: targetScopeId(target),
  });
  const reach = impact.isLoading
    ? t('parameters.impact.loading', 'Working out which terminals this reaches…')
    : impact.isError || impact.data === undefined
      ? t('parameters.impact.error', 'Could not work out which terminals this reaches.')
      : impactSentence(impact.data, target);
  return (
    <p role="note" className="rounded-md border bg-secondary/40 p-2 text-xs">
      {reach} {timingStatement(t, descriptor)}
    </p>
  );
}

/**
 * What the till itself reports for a shift-boundary parameter (RL-267): the
 * value it operates under now and any value it is holding for the next shift
 * boundary. Shown at terminal scope only, from the till's last telemetry, so
 * a held change is visible rather than inferred from "authored ≠ in effect".
 */
function TillReportLine({
  descriptor,
  terminalId,
}: {
  descriptor: TerminalParamDescriptor;
  terminalId: string;
}) {
  const t = useT();
  const lag = useSyncLag(terminalId);
  if (lag.data === undefined) return null;
  const report = lag.data.shiftBoundaryParams?.find((entry) => entry.key === descriptor.key);
  if (report === undefined) {
    return (
      <p className="mt-1 text-xs text-muted-foreground">
        {t(
          'parameters.editor.tillNotReported',
          'The till has not reported which value it is operating under.',
        )}
      </p>
    );
  }
  return (
    <p className="mt-1 text-xs text-muted-foreground" data-till-report={descriptor.key}>
      {t('parameters.editor.tillOperatesUnder', 'On the till now:')}{' '}
      {formatParamValue(descriptor, report.value, valueWords(t))}
      {report.pendingValue !== null && (
        <>
          {' · '}
          {t('parameters.editor.tillHolding', 'held for the next shift boundary:')}{' '}
          {formatParamValue(descriptor, report.pendingValue, valueWords(t))}
        </>
      )}
    </p>
  );
}

/**
 * The inheritance line (TP-FR-016), in the terminal-config editor's own
 * vocabulary: overridden here, inherited from a named scope with that value,
 * or running on the registry default. "Which scope won" is the answer to
 * "I set it and nothing happened", which used to need an API call.
 */
function InheritanceLine({
  descriptor,
  target,
  resolution,
}: {
  descriptor: TerminalParamDescriptor;
  target: EditorTarget;
  resolution: RowResolution;
}) {
  const t = useT();
  const { setHere, effective, beneath } = resolution;
  if (setHere !== undefined) {
    // RL-423: a keep-current key has no default beneath to fall back to.
    const overrides = keepsCurrent(descriptor, beneath)
      ? t(
          'parameters.editor.nothingBeneathKeepsCurrent',
          'nothing is set at a wider scope; after clearing, the till keeps its current setting',
        )
      : beneath.source === 'default'
        ? `${t('parameters.editor.overridesDefault', 'overrides the registry default')} ${formatParamValue(descriptor, beneath.value, valueWords(t))}`
        : `${t('parameters.editor.overrides', 'overrides')} ${sourceLabel(beneath.source, target, sourceWords(t))}: ${formatParamValue(descriptor, beneath.value, valueWords(t))}`;
    return (
      <p className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <Badge variant="success">
          {target.scope === 'global'
            ? t('parameters.editor.setHere', 'Set here')
            : t('parameters.editor.overriddenHere', 'Overridden here')}
        </Badge>
        <span>{overrides}</span>
      </p>
    );
  }
  if (keepsCurrent(descriptor, effective)) {
    // RL-423: the till acts only on an authored value, so the registry
    // default is not what it runs under — say so rather than show "off".
    return (
      <p className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <Badge variant="outline">{t('parameters.editor.notSet', 'Not set')}</Badge>
        <span>
          {t('parameters.editor.keepsCurrent', 'the till keeps its current setting')} ·{' '}
          {t('parameters.editor.nothingSetAnywhere', 'nothing is set at any scope')}
        </span>
      </p>
    );
  }
  if (effective.source === 'default') {
    return (
      <p className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <Badge variant="outline">
          {t('parameters.editor.registryDefault', 'Registry default')}
        </Badge>
        <span>
          {formatParamValue(descriptor, effective.value, valueWords(t))} ·{' '}
          {t('parameters.editor.nothingSetAnywhere', 'nothing is set at any scope')}
        </span>
      </p>
    );
  }
  return (
    <p className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
      <Badge>{t('parameters.editor.inherited', 'Inherited')}</Badge>
      <span>
        {t('parameters.editor.from', 'from')}{' '}
        {sourceLabel(effective.source, target, sourceWords(t))}:{' '}
        {formatParamValue(descriptor, effective.value, valueWords(t))}
      </span>
    </p>
  );
}

/**
 * Who changed this parameter at this scope, when, and from what (TP-FR-022).
 * Read-only: there is no revert control, because reverting is authoring the
 * previous value, which is itself audited (TP-FR-023).
 */
function HistoryPanel({
  descriptor,
  target,
}: {
  descriptor: TerminalParamDescriptor;
  target: EditorTarget;
}) {
  const t = useT();
  const history = useTerminalParamHistory({
    key: descriptor.key,
    scope: target.scope,
    scopeId: targetScopeId(target),
  });
  const format = (value: TerminalParamValue | null) =>
    value === null ? '—' : formatParamValue(descriptor, value, valueWords(t));
  if (history.isLoading) {
    return (
      <p className="text-xs text-muted-foreground">
        {t('parameters.editor.historyLoading', 'Loading history…')}
      </p>
    );
  }
  if (history.isError || history.data === undefined) {
    return (
      <p className="text-xs text-destructive">
        {t('parameters.editor.historyError', 'Could not load history.')}
      </p>
    );
  }
  if (history.data.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        {t('parameters.editor.historyEmpty', 'No changes recorded at this scope.')}
      </p>
    );
  }
  return (
    <ol
      className="space-y-1 text-xs"
      aria-label={`${t('parameters.editor.historyOf', 'History of')} ${descriptor.label}`}
    >
      {history.data.map((entry) => (
        <li
          key={entry.id}
          className="flex flex-wrap gap-x-2 rounded border bg-background px-2 py-1"
        >
          <span className="font-mono text-muted-foreground">
            {new Date(entry.occurredAt).toLocaleString()}
          </span>
          <span>
            {entry.actorEmail ?? entry.actorUserId ?? t('parameters.editor.system', 'system')}
          </span>
          <span>
            {entry.action === 'clear'
              ? `${t('parameters.editor.clearedWas', 'cleared (was')} ${format(entry.from)})`
              : descriptor.secret !== undefined
                ? entry.from === null
                  ? t('parameters.secret.historySet', 'password set')
                  : t('parameters.secret.historyChanged', 'password changed')
                : `${format(entry.from)} → ${format(entry.to)}`}
          </span>
        </li>
      ))}
    </ol>
  );
}

function ParameterRow({
  descriptor,
  target,
  resolution,
  canWrite,
  inactive,
}: {
  descriptor: TerminalParamDescriptor;
  target: EditorTarget;
  resolution: RowResolution;
  canWrite: boolean;
  /** Set when the parameter has no effect here (VL-365): shown, not editable. */
  inactive?: InactiveReason | undefined;
}) {
  const t = useT();
  descriptor = {
    ...descriptor,
    label: t(`params.${descriptor.key}.label`, descriptor.label),
    description:
      descriptor.description === undefined
        ? undefined
        : t(`params.${descriptor.key}.description`, descriptor.description),
  };
  const { setHere, effective } = resolution;
  const canReadTerminals = useHasPermission('terminal-management.read');
  const setParam = useSetTerminalParam();
  const clearParam = useClearTerminalParam();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [fieldEpoch, setFieldEpoch] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [confirmingClear, setConfirmingClear] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const scopeId = targetScopeId(target);
  const authorable = descriptor.authorable && !descriptor.deprecated;
  const editable = canWrite && authorable && inactive === undefined;
  // The field starts from what the target resolves today — inherited or
  // default — so overriding one value does not mean retyping it from blank.
  const current: TerminalParamValue = effective.value;
  // RL-423: set nowhere, and the till then leaves the device as it is.
  const unset = keepsCurrent(descriptor, effective);
  const dirty = draft !== null;
  const draftError =
    draft === null ? null : (draft.error ?? validateParamValue(descriptor, draft.value));
  const pending = setParam.isPending || clearParam.isPending;
  const fieldId = `param-${descriptor.key.replace(/\./g, '-')}`;

  const resetField = () => {
    setDraft(null);
    setFieldEpoch((epoch) => epoch + 1);
  };

  const save = async () => {
    if (draft === null || draftError !== null) return;
    setError(null);
    setSaved(false);
    try {
      await setParam.mutateAsync({
        key: descriptor.key,
        scope: target.scope,
        scopeId,
        value: draft.value,
      });
      resetField();
      setSaved(true);
    } catch (cause) {
      setError(extractApiError(cause));
    }
  };

  const clear = async () => {
    setError(null);
    setSaved(false);
    try {
      await clearParam.mutateAsync({ key: descriptor.key, scope: target.scope, scopeId });
      resetField();
      setConfirmingClear(false);
    } catch (cause) {
      setError(extractApiError(cause));
    }
  };

  return (
    <div
      data-param={descriptor.key}
      className="grid gap-3 px-5 py-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]"
    >
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium">{descriptor.label}</span>
          {descriptor.deprecated && (
            <Badge variant="outline">{t('parameters.editor.deprecated', 'Deprecated')}</Badge>
          )}
          {!descriptor.authorable && (
            <Badge variant="outline">{t('parameters.editor.machineOwned', 'Machine-owned')}</Badge>
          )}
          {descriptor.appliesAt === 'shiftBoundary' && (
            <Badge variant="outline">
              {t('parameters.editor.appliesAtShiftBoundary', 'Applies at next shift boundary')}
            </Badge>
          )}
        </div>
        <p className="font-mono text-xs text-muted-foreground">{descriptor.key}</p>
        {descriptor.description !== undefined && (
          <p className="mt-1 text-sm text-muted-foreground">{descriptor.description}</p>
        )}
        <InheritanceLine descriptor={descriptor} target={target} resolution={resolution} />
        {inactive !== undefined && (
          <p className="mt-1 text-xs text-amber-700" data-inactive={descriptor.key}>
            {t('parameters.editor.inactiveWhen', 'Used only when')} {inactive.controllerLabel}:{' '}
            {inactive.values.join(', ')}.{' '}
            {t('parameters.editor.inactiveHere', 'It has no effect here.')}
          </p>
        )}
        {descriptor.appliesAt === 'shiftBoundary' &&
          target.scope === 'terminal' &&
          canReadTerminals && (
            <TillReportLine descriptor={descriptor} terminalId={target.terminalId} />
          )}
        <button
          type="button"
          className="mt-2 text-xs text-muted-foreground underline-offset-2 hover:underline"
          aria-label={`${t('parameters.editor.historyOf', 'History of')} ${descriptor.label}`}
          aria-expanded={showHistory}
          onClick={() => setShowHistory((open) => !open)}
        >
          {showHistory
            ? t('parameters.editor.hideHistory', 'Hide history')
            : t('parameters.editor.history', 'History')}
        </button>
        {showHistory && (
          <div className="mt-2">
            <HistoryPanel descriptor={descriptor} target={target} />
          </div>
        )}
      </div>

      <div className="space-y-2">
        {editable && descriptor.secret !== undefined ? (
          <SecretParamControl
            id={fieldId}
            descriptor={descriptor}
            scope={target.scope}
            scopeId={scopeId}
            setHere={setHere}
            disabled={pending}
            impact={<ImpactNote descriptor={descriptor} target={target} />}
          />
        ) : editable ? (
          <ParamField
            key={fieldEpoch}
            id={fieldId}
            descriptor={descriptor}
            value={draft?.value ?? current}
            unset={unset ? { selected: draft === null } : undefined}
            disabled={pending}
            onChange={(value, fieldError) => {
              setDraft({ value, error: fieldError });
              setSaved(false);
            }}
          />
        ) : !descriptor.authorable && SPECIAL_VIEWS[descriptor.key] !== undefined ? (
          (() => {
            const SpecialView = SPECIAL_VIEWS[descriptor.key];
            return SpecialView === undefined ? null : (
              <SpecialView descriptor={descriptor} assignment={setHere} />
            );
          })()
        ) : (
          <p className="text-sm" aria-label={descriptor.label}>
            {unset
              ? t(
                  'parameters.field.notSetKeepsCurrent',
                  'Not set — the till keeps its current setting',
                )
              : formatParamValue(descriptor, current, valueWords(t))}
          </p>
        )}
        {!descriptor.authorable && descriptor.provenance !== undefined && (
          <p className="text-xs text-muted-foreground">{descriptor.provenance}</p>
        )}
        {draftError !== null && <p className="text-sm text-destructive">{draftError}</p>}
        {error !== null && <p className="text-sm text-destructive">{error}</p>}
        {saved && (
          <p className="text-xs text-emerald-700">{t('parameters.editor.saved', 'Saved.')}</p>
        )}
        {dirty && <ImpactNote descriptor={descriptor} target={target} />}
        {canWrite && descriptor.authorable && (
          <div className="flex flex-wrap items-center gap-2">
            {!descriptor.deprecated && descriptor.secret === undefined && (
              <Button
                type="button"
                size="sm"
                aria-label={`${t('common.save', 'Save')} ${descriptor.label}`}
                disabled={!dirty || draftError !== null || pending}
                onClick={() => void save()}
              >
                {setParam.isPending ? t('common.saving', 'Saving…') : t('common.save', 'Save')}
              </Button>
            )}
            {dirty && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                aria-label={`${t('common.cancel', 'Cancel')} ${descriptor.label}`}
                disabled={pending}
                onClick={resetField}
              >
                {t('common.cancel', 'Cancel')}
              </Button>
            )}
            {setHere !== undefined && !confirmingClear && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                aria-label={`${t('parameters.editor.clearAction', 'Clear')} ${descriptor.label}`}
                disabled={pending}
                onClick={() => {
                  setError(null);
                  setSaved(false);
                  setConfirmingClear(true);
                }}
              >
                {t('parameters.editor.clear', 'Clear…')}
              </Button>
            )}
          </div>
        )}
        {confirmingClear && setHere !== undefined && (
          <div
            role="group"
            aria-label={`${t('parameters.editor.clearAction', 'Clear')} ${descriptor.label}`}
            className="space-y-2 rounded-md border bg-background p-3"
          >
            <p className="text-sm">
              {clearOutcome(
                (value) => formatParamValue(descriptor, value, valueWords(t)),
                target,
                resolution,
                clearWords(t),
                descriptor,
              )}
            </p>
            <ImpactNote descriptor={descriptor} target={target} />
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                variant="destructive"
                aria-label={`${t('parameters.editor.confirmClear', 'Confirm clear')} ${descriptor.label}`}
                disabled={pending}
                onClick={() => void clear()}
              >
                {clearParam.isPending
                  ? t('parameters.editor.clearing', 'Clearing…')
                  : t('parameters.editor.clearValue', 'Clear value')}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                aria-label={`${t('parameters.editor.keep', 'Keep')} ${descriptor.label}`}
                disabled={pending}
                onClick={() => setConfirmingClear(false)}
              >
                {t('parameters.editor.keepValue', 'Keep value')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export { targetScopeId, type EditorTarget } from './editor-target';
